import { useQuery, useMutation } from "@tanstack/react-query";
import type { UseQueryOptions, UseMutationOptions } from "@tanstack/react-query";
import { customFetch } from "./custom-fetch";
import type { ErrorType } from "./custom-fetch";

export type ProjectStatus = "lead" | "quoted" | "approved" | "in_progress" | "complete" | "archived";
export type ProjectCostType = "labour" | "materials" | "fees" | "subcontractor" | "equipment" | "other";

export interface Project {
  id: number;
  userId: number;
  name: string;
  customerId: number | null;
  status: ProjectStatus;
  startDate: string | null;
  targetCompletionDate: string | null;
  notes: string | null;
  lastActivityAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectListItem extends Project {
  customerName: string | null;
  quotedTotal: number;
  actualTotal: number;
  invoicedTotal: number;
  paidTotal: number;
  outstanding: number;
  profit: number;
  margin: number;
}

export interface ProjectDetail extends Project {
  customer: {
    id: number;
    name: string;
    email: string | null;
    phone: string | null;
    address: string | null;
  } | null;
}

export interface ProjectLineItem {
  name: string;
  type: string;
  quoted: number;
  actual: number;
  variance: number;
}

export interface ProjectDocumentLinks {
  quotes: Record<string, { projectId: number; projectName: string }>;
  invoices: Record<string, { projectId: number; projectName: string }>;
}

export interface ProjectAggregates {
  quotedTotal: number;
  quotedInternalCost: number;
  quotedByCategory: Record<string, number>;
  actualTotal: number;
  actualByCategory: Record<string, number>;
  invoicedTotal: number;
  paidTotal: number;
  outstanding: number;
  projectedProfit: number;
  actualProfit: number;
  margin: number;
  quotedMargin: number;
  variance: number;
  variancePct: number;
  lineItems: ProjectLineItem[];
  pendingCoTotal: number;
  pendingCoInternalCost: number;
  pendingCoCount: number;
}

export interface ProjectQuote {
  id: number;
  estimateNumber: string | null;
  title: string;
  status: string;
  estimateDate: string | null;
  customerName: string | null;
  total: number;
}

export interface ProjectInvoice {
  id: number;
  invoiceNumber: string | null;
  title: string;
  status: string;
  invoiceDate: string | null;
  dueDate: string | null;
  customerName: string | null;
  total: number;
  paid: number;
  isDepositChild: boolean;
  depositParentId: number | null;
  isMilestoneParent: boolean;
  milestoneParentId: number | null;
}

export interface ProjectActualCost {
  id: number;
  projectId: number;
  catalogItemId: number | null;
  lineItemId: number | null;
  sourceMaterialId: number | null;
  name: string;
  description: string | null;
  vendor: string | null;
  date: string | null;
  quantity: number;
  unit: string | null;
  unitCost: number;
  quotedUnitCost: number | null;
  type: ProjectCostType;
  category: string | null;
  amount: number;
  /**
   * Quote labour that the crew's time log has replaced: shown for comparison,
   * left out of actual cost. Only set by the list endpoint.
   */
  replacedByTimeLog?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateProjectActualCostBody {
  name: string;
  description?: string | null;
  vendor?: string | null;
  date?: string | null;
  quantity?: number;
  unit?: string | null;
  unitCost?: number;
  type?: ProjectCostType;
  category?: string | null;
  catalogItemId?: number | null;
  lineItemId?: number | null;
}

export interface ProjectFile {
  id: number;
  userId: number;
  projectId: number;
  fileName: string;
  contentType: string;
  objectPath: string;
  createdAt: string;
}

export interface ProjectActivity {
  id: number;
  projectId: number;
  actorUserId: number | null;
  type: string;
  payload: Record<string, unknown> | null;
  createdAt: string;
}

export interface CreateProjectBody {
  name: string;
  customerId?: number | null;
  status?: ProjectStatus;
  startDate?: string | null;
  targetCompletionDate?: string | null;
  notes?: string | null;
}

export type UpdateProjectBody = Partial<CreateProjectBody>;

export interface ListProjectsParams {
  status?: ProjectStatus;
  customerId?: number;
  fromDate?: string;
  toDate?: string;
  search?: string;
}

const qs = (params: Record<string, unknown> | undefined): string => {
  if (!params) return "";
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
};

export const getListProjectsQueryKey = (params?: ListProjectsParams) =>
  ["/api/projects", params ?? {}] as const;
export const getGetProjectQueryKey = (id: number) =>
  [`/api/projects/${id}`] as const;
export const getProjectAggregatesQueryKey = (id: number) =>
  [`/api/projects/${id}/aggregates`] as const;
export const getProjectQuotesQueryKey = (id: number) =>
  [`/api/projects/${id}/quotes`] as const;
export const getProjectInvoicesQueryKey = (id: number) =>
  [`/api/projects/${id}/invoices`] as const;
export const getProjectActualCostsQueryKey = (id: number) =>
  [`/api/projects/${id}/actual-costs`] as const;
export const getProjectFilesQueryKey = (id: number) =>
  [`/api/projects/${id}/files`] as const;
export const getProjectActivityQueryKey = (id: number) =>
  [`/api/projects/${id}/activity`] as const;
export const getProjectCalendarQueryKey = () =>
  ["/api/projects/calendar"] as const;

export interface ProjectCalendarItem {
  id: number;
  name: string;
  status: ProjectStatus;
  startDate: string | null;
  targetCompletionDate: string | null;
  customerId: number | null;
  customerName: string | null;
}

export const listProjectCalendar = () =>
  customFetch<ProjectCalendarItem[]>("/api/projects/calendar");

export const useProjectCalendar = <TData = ProjectCalendarItem[], TError = ErrorType<unknown>>(
  options?: { query?: UseQueryOptions<ProjectCalendarItem[], TError, TData> },
) => {
  const queryKey = options?.query?.queryKey ?? getProjectCalendarQueryKey();
  return useQuery<ProjectCalendarItem[], TError, TData>({
    queryKey,
    queryFn: () => listProjectCalendar(),
    ...options?.query,
  });
};

// ---- Calendar subscription (iCal export) ----
export interface ProjectCalendarSubscription {
  subscriptionUrl: string;
  downloadUrl: string;
}

export const getProjectCalendarSubscriptionQueryKey = () =>
  ["/api/projects/calendar/subscription"] as const;

export const getProjectCalendarSubscription = () =>
  customFetch<ProjectCalendarSubscription>("/api/projects/calendar/subscription");

export const useProjectCalendarSubscription = <
  TData = ProjectCalendarSubscription,
  TError = ErrorType<unknown>,
>(
  options?: { query?: UseQueryOptions<ProjectCalendarSubscription, TError, TData> },
) => {
  const queryKey = options?.query?.queryKey ?? getProjectCalendarSubscriptionQueryKey();
  return useQuery<ProjectCalendarSubscription, TError, TData>({
    queryKey,
    queryFn: () => getProjectCalendarSubscription(),
    ...options?.query,
  });
};

export const rotateProjectCalendarSubscription = () =>
  customFetch<ProjectCalendarSubscription>(
    "/api/projects/calendar/subscription/rotate",
    { method: "POST" },
  );

export const useRotateProjectCalendarSubscription = <TError = ErrorType<unknown>>(
  options?: UseMutationOptions<ProjectCalendarSubscription, TError, void>,
) => useMutation({ mutationFn: rotateProjectCalendarSubscription, ...options });

// ---- Projects ----
export const listProjects = (params?: ListProjectsParams) =>
  customFetch<ProjectListItem[]>(`/api/projects${qs(params as Record<string, unknown>)}`);

export const useListProjects = <TData = ProjectListItem[], TError = ErrorType<unknown>>(
  params?: ListProjectsParams,
  options?: { query?: UseQueryOptions<ProjectListItem[], TError, TData> },
) => {
  const queryKey = options?.query?.queryKey ?? getListProjectsQueryKey(params);
  return useQuery<ProjectListItem[], TError, TData>({
    queryKey,
    queryFn: () => listProjects(params),
    ...options?.query,
  });
};

export const getProject = (id: number) =>
  customFetch<ProjectDetail>(`/api/projects/${id}`);

export const useGetProject = <TData = ProjectDetail, TError = ErrorType<unknown>>(
  id: number,
  options?: { query?: UseQueryOptions<ProjectDetail, TError, TData> },
) => {
  const queryKey = options?.query?.queryKey ?? getGetProjectQueryKey(id);
  return useQuery<ProjectDetail, TError, TData>({
    queryKey,
    queryFn: () => getProject(id),
    enabled: !!id,
    ...options?.query,
  });
};

export const createProject = (data: CreateProjectBody) =>
  customFetch<Project>(`/api/projects`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });

export const useCreateProject = <TError = ErrorType<unknown>>(
  options?: UseMutationOptions<Project, TError, CreateProjectBody>,
) => useMutation({ mutationFn: createProject, ...options });

export const updateProject = (id: number, data: UpdateProjectBody) =>
  customFetch<Project>(`/api/projects/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });

export const useUpdateProject = <TError = ErrorType<unknown>>(
  options?: UseMutationOptions<Project, TError, { id: number; data: UpdateProjectBody }>,
) => useMutation({
  mutationFn: ({ id, data }) => updateProject(id, data),
  ...options,
});

export const deleteProject = (id: number) =>
  customFetch<void>(`/api/projects/${id}`, { method: "DELETE" });

export const useDeleteProject = <TError = ErrorType<unknown>>(
  options?: UseMutationOptions<void, TError, number>,
) => useMutation({ mutationFn: deleteProject, ...options });

// ---- Aggregates ----
export const getProjectAggregates = (id: number) =>
  customFetch<ProjectAggregates>(`/api/projects/${id}/aggregates`);

export const useProjectAggregates = (id: number) =>
  useQuery({
    queryKey: getProjectAggregatesQueryKey(id),
    queryFn: () => getProjectAggregates(id),
    enabled: !!id,
  });

// ---- Document links (project info per quote/invoice) ----
export const getProjectDocumentLinksQueryKey = () =>
  ["/api/projects/document-links"] as const;

export const getProjectDocumentLinks = () =>
  customFetch<ProjectDocumentLinks>("/api/projects/document-links");

export const useProjectDocumentLinks = () =>
  useQuery({
    queryKey: getProjectDocumentLinksQueryKey(),
    queryFn: () => getProjectDocumentLinks(),
  });

// ---- Quotes / Invoices attachment ----
export const listProjectQuotes = (id: number) =>
  customFetch<ProjectQuote[]>(`/api/projects/${id}/quotes`);

export const useProjectQuotes = (id: number) =>
  useQuery({
    queryKey: getProjectQuotesQueryKey(id),
    queryFn: () => listProjectQuotes(id),
    enabled: !!id,
  });

export const attachQuote = (projectId: number, quoteId: number) =>
  customFetch<{ ok: true; seededCosts: number }>(
    `/api/projects/${projectId}/quotes/${quoteId}`,
    { method: "POST" },
  );

export const useAttachQuote = () =>
  useMutation({
    mutationFn: ({ projectId, quoteId }: { projectId: number; quoteId: number }) =>
      attachQuote(projectId, quoteId),
  });

export const detachQuote = (projectId: number, quoteId: number) =>
  customFetch<void>(`/api/projects/${projectId}/quotes/${quoteId}`, { method: "DELETE" });

export const useDetachQuote = () =>
  useMutation({
    mutationFn: ({ projectId, quoteId }: { projectId: number; quoteId: number }) =>
      detachQuote(projectId, quoteId),
  });

export const listProjectInvoices = (id: number) =>
  customFetch<ProjectInvoice[]>(`/api/projects/${id}/invoices`);

export const useProjectInvoices = (id: number) =>
  useQuery({
    queryKey: getProjectInvoicesQueryKey(id),
    queryFn: () => listProjectInvoices(id),
    enabled: !!id,
  });

export const attachInvoice = (projectId: number, invoiceId: number) =>
  customFetch<{ ok: true }>(`/api/projects/${projectId}/invoices/${invoiceId}`, { method: "POST" });

export const useAttachInvoice = () =>
  useMutation({
    mutationFn: ({ projectId, invoiceId }: { projectId: number; invoiceId: number }) =>
      attachInvoice(projectId, invoiceId),
  });

export const detachInvoice = (projectId: number, invoiceId: number) =>
  customFetch<void>(`/api/projects/${projectId}/invoices/${invoiceId}`, { method: "DELETE" });

export const useDetachInvoice = () =>
  useMutation({
    mutationFn: ({ projectId, invoiceId }: { projectId: number; invoiceId: number }) =>
      detachInvoice(projectId, invoiceId),
  });

export const resyncProjectFromQuotes = (projectId: number) =>
  customFetch<{ added: number }>(`/api/projects/${projectId}/resync-from-quotes`, { method: "POST" });

export const useResyncProjectFromQuotes = () =>
  useMutation({ mutationFn: resyncProjectFromQuotes });

// ---- Actual costs ----
export const listProjectActualCosts = (id: number) =>
  customFetch<ProjectActualCost[]>(`/api/projects/${id}/actual-costs`);

export const useProjectActualCosts = (id: number) =>
  useQuery({
    queryKey: getProjectActualCostsQueryKey(id),
    queryFn: () => listProjectActualCosts(id),
    enabled: !!id,
  });

export const createProjectActualCost = (id: number, data: CreateProjectActualCostBody) =>
  customFetch<ProjectActualCost>(`/api/projects/${id}/actual-costs`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });

export const useCreateProjectActualCost = () =>
  useMutation({
    mutationFn: ({ id, data }: { id: number; data: CreateProjectActualCostBody }) =>
      createProjectActualCost(id, data),
  });

export const updateProjectActualCost = (id: number, costId: number, data: Partial<CreateProjectActualCostBody>) =>
  customFetch<ProjectActualCost>(`/api/projects/${id}/actual-costs/${costId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });

export const useUpdateProjectActualCost = () =>
  useMutation({
    mutationFn: ({ id, costId, data }: { id: number; costId: number; data: Partial<CreateProjectActualCostBody> }) =>
      updateProjectActualCost(id, costId, data),
  });

export const deleteProjectActualCost = (id: number, costId: number) =>
  customFetch<void>(`/api/projects/${id}/actual-costs/${costId}`, { method: "DELETE" });

export const useDeleteProjectActualCost = () =>
  useMutation({
    mutationFn: ({ id, costId }: { id: number; costId: number }) =>
      deleteProjectActualCost(id, costId),
  });

// ---- Files ----
export const listProjectFiles = (id: number) =>
  customFetch<ProjectFile[]>(`/api/projects/${id}/files`);

export const useProjectFiles = (id: number) =>
  useQuery({
    queryKey: getProjectFilesQueryKey(id),
    queryFn: () => listProjectFiles(id),
    enabled: !!id,
  });

export const createProjectFile = (id: number, data: { fileName: string; contentType: string; objectPath: string }) =>
  customFetch<ProjectFile>(`/api/projects/${id}/files`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });

export const useCreateProjectFile = () =>
  useMutation({
    mutationFn: ({ id, data }: { id: number; data: { fileName: string; contentType: string; objectPath: string } }) =>
      createProjectFile(id, data),
  });

export const deleteProjectFile = (id: number, fileId: number) =>
  customFetch<void>(`/api/projects/${id}/files/${fileId}`, { method: "DELETE" });

export const useDeleteProjectFile = () =>
  useMutation({
    mutationFn: ({ id, fileId }: { id: number; fileId: number }) =>
      deleteProjectFile(id, fileId),
  });

// ---- Activity ----
export const listProjectActivity = (id: number) =>
  customFetch<ProjectActivity[]>(`/api/projects/${id}/activity`);

export const useProjectActivity = (id: number) =>
  useQuery({
    queryKey: getProjectActivityQueryKey(id),
    queryFn: () => listProjectActivity(id),
    enabled: !!id,
  });

// ---- Change Order Items ----
export type CoItemStatus = "pending" | "attached";
export type CoItemType = "labour" | "materials" | "fees";

export interface ProjectChangeOrderItem {
  id: number;
  projectId: number;
  userId: number;
  description: string;
  quantity: number;
  unit: string | null;
  unitPrice: number;
  unitCost: number;
  type: CoItemType;
  catalogItemId: number | null;
  status: CoItemStatus;
  attachedToEstimateId: number | null;
  attachedToInvoiceId: number | null;
  sortOrder: number;
  total: number;
  totalCost: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateChangeOrderItemBody {
  description: string;
  quantity?: number;
  unit?: string | null;
  unitPrice?: number;
  unitCost?: number;
  type?: CoItemType;
  catalogItemId?: number | null;
  sortOrder?: number;
}

export type UpdateChangeOrderItemBody = Partial<CreateChangeOrderItemBody>;

export interface AttachChangeOrderItemsBody {
  itemIds: number[];
  targetType: "estimate" | "invoice";
  targetId: number;
  groupName?: string;
}

export const getProjectChangeOrderItemsQueryKey = (id: number) =>
  [`/api/projects/${id}/change-order-items`] as const;

export const listProjectChangeOrderItems = (id: number) =>
  customFetch<ProjectChangeOrderItem[]>(`/api/projects/${id}/change-order-items`);

export const useProjectChangeOrderItems = (id: number) =>
  useQuery({
    queryKey: getProjectChangeOrderItemsQueryKey(id),
    queryFn: () => listProjectChangeOrderItems(id),
    enabled: !!id,
  });

export const createProjectChangeOrderItem = (id: number, data: CreateChangeOrderItemBody) =>
  customFetch<ProjectChangeOrderItem>(`/api/projects/${id}/change-order-items`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });

export const useCreateProjectChangeOrderItem = () =>
  useMutation({
    mutationFn: ({ id, data }: { id: number; data: CreateChangeOrderItemBody }) =>
      createProjectChangeOrderItem(id, data),
  });

export const updateProjectChangeOrderItem = (id: number, itemId: number, data: UpdateChangeOrderItemBody) =>
  customFetch<ProjectChangeOrderItem>(`/api/projects/${id}/change-order-items/${itemId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });

export const useUpdateProjectChangeOrderItem = () =>
  useMutation({
    mutationFn: ({ id, itemId, data }: { id: number; itemId: number; data: UpdateChangeOrderItemBody }) =>
      updateProjectChangeOrderItem(id, itemId, data),
  });

export const deleteProjectChangeOrderItem = (id: number, itemId: number) =>
  customFetch<void>(`/api/projects/${id}/change-order-items/${itemId}`, { method: "DELETE" });

export const useDeleteProjectChangeOrderItem = () =>
  useMutation({
    mutationFn: ({ id, itemId }: { id: number; itemId: number }) =>
      deleteProjectChangeOrderItem(id, itemId),
  });

export const attachChangeOrderItems = (id: number, data: AttachChangeOrderItemsBody) =>
  customFetch<{ ok: true; groupId: number; attached: number }>(
    `/api/projects/${id}/change-order-items/attach`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    },
  );

export const useAttachChangeOrderItems = () =>
  useMutation({
    mutationFn: ({ id, data }: { id: number; data: AttachChangeOrderItemsBody }) =>
      attachChangeOrderItems(id, data),
  });

// ---------------------------------------------------------------------------
// CO staging estimate
// ---------------------------------------------------------------------------

export const getProjectCoEstimateQueryKey = (id: number) =>
  [`/api/projects/${id}/change-order-estimate`] as const;

export const getProjectCoEstimate = (id: number) =>
  customFetch<{ id: number }>(`/api/projects/${id}/change-order-estimate`);

export const useGetProjectCoEstimate = (id: number) =>
  useQuery({
    queryKey: getProjectCoEstimateQueryKey(id),
    queryFn: () => getProjectCoEstimate(id),
    enabled: !!id,
  });

export const attachCoItems = (
  projectId: number,
  body: { lineItemIds: number[]; groupIds: number[]; targetEstimateId?: number; targetInvoiceId?: number },
) =>
  customFetch<{ ok: true; copied: number }>(`/api/projects/${projectId}/attach-co-items`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

export const useAttachCoItems = () =>
  useMutation({
    mutationFn: ({
      projectId,
      lineItemIds,
      groupIds,
      targetEstimateId,
      targetInvoiceId,
    }: {
      projectId: number;
      lineItemIds: number[];
      groupIds: number[];
      targetEstimateId?: number;
      targetInvoiceId?: number;
    }) => attachCoItems(projectId, { lineItemIds, groupIds, targetEstimateId, targetInvoiceId }),
  });
